import { FieldError } from 'react-hook-form';
import { MultipleFieldErrors } from 'react-hook-form/dist/types/errors';

import { trackEvent } from '@xingternal/hops-tracking';

import { AvailableFormFieldValues, CareerStage, Education } from '@/domain/job-application/types';
import { trackNwtEvent } from '@/helpers/nwt-tracking';

type SubmitValidationValues = Record<Exclude<keyof AvailableFormFieldValues, 'cvData'>, MultipleFieldErrors> & {
  cvData: {
    overallExperience: MultipleFieldErrors;
    careerStage: Record<keyof CareerStage, MultipleFieldErrors>;
    education: Record<keyof Education, MultipleFieldErrors>;
  };
};

const propTrackActionErrorsByFieldName: SubmitValidationValues = {
  firstName: {
    required: 'jobs_first_name_error_empty_field',
    validate: 'jobs_first_name_error_validation_field',
  },
  lastName: {
    required: 'jobs_last_name_error_empty_field',
    validate: 'jobs_last_name_error_validation_field',
  },
  email: {
    required: 'jobs_email_error_empty_field',
    validate: 'jobs_email_error_validation_field',
  },
  countryCode: {
    required: 'jobs_country_code_error_empty_field',
  },
  phone: {
    required: 'jobs_phone_number_error_empty_field',
    validate: 'jobs_phone_number_error_validation_field',
  },
  cvDoc: {
    required: 'jobs_upload_error_empty_field',
  },
  otherDocs: { required: '' },
  comments: {
    required: 'jobs_comments_error_length_exceeded_field',
    validate: 'jobs_comments_error_length_exceeded_field',
  },
  enterCvMode: {
    required: '',
  },
  cvData: {
    overallExperience: {
      required: 'jobs_cv_experience_error_empty_field',
    },
    careerStage: {
      position: {
        required: 'jobs_cv_job_title_error_empty_field',
        validate: 'jobs_cv_job_title_error_validation_field',
      },
      employer: {
        required: 'jobs_cv_company_name_error_empty_field',
        validate: 'jobs_cv_company_name_error_validation_field',
      },
      startDate: { required: 'jobs_cv_company_start_date_error_empty_field' },
      endDate: { required: 'jobs_cv_company_end_date_error_empty_field' },
      isPresentEndDate: { required: '' },
    },
    education: {
      degree: {
        required: 'jobs_cv_degree_error_empty_field',
        validate: 'jobs_cv_degree_error_validation_field',
      },
      institution: {
        required: 'jobs_cv_school_error_empty_field',
        validate: 'jobs_cv_school_error_validation_field',
      },
      startDate: { required: 'jobs_cv_degree_from_error_empty_field' },
      endDate: { required: 'jobs_cv_degree_to_error_empty_field' },
      isPresentEndDate: { required: '' },
    },
  },
};

const nwtContextErrorsByFieldName: SubmitValidationValues = {
  firstName: {
    required: 'instant_apply_submit_error_first_name_empty_field',
    validate: 'instant_apply_submit_error_first_name_validation_field',
  },
  lastName: {
    required: 'instant_apply_submit_error_last_name_empty_field',
    validate: 'instant_apply_submit_error_last_name_validation_field',
  },
  email: {
    required: 'instant_apply_submit_error_email_empty_field',
    validate: 'instant_apply_submit_error_email_validation_field',
  },
  countryCode: { required: 'jobs_country_code_error_empty_field' },
  phone: {
    required: 'instant_apply_submit_error_phone_number_empty_field',
    validate: 'instant_apply_submit_error_phone_number_validation_field',
  },
  cvDoc: { required: 'instant_apply_submit_error_upload_cv_empty_field' },
  otherDocs: { required: '' },
  comments: {
    required: 'instant_apply_submit_error_comments_length_exceeded_field',
    validate: 'instant_apply_submit_error_comments_length_exceeded_field',
  },
  enterCvMode: { required: '' },
  cvData: {
    overallExperience: { required: 'instant_apply_submit_error_experience_empty_field' },
    careerStage: {
      position: {
        required: 'instant_apply_submit_error_job_title_empty_field',
        validate: 'instant_apply_submit_error_job_title_validation_field',
      },
      employer: {
        required: 'instant_apply_submit_error_company_name_empty_field',
        validate: 'instant_apply_submit_error_company_name_validation_field',
      },
      startDate: { required: 'instant_apply_submit_error_company_start_date_empty_field' },
      endDate: { required: 'instant_apply_submit_error_company_end_date_empty_field' },
      isPresentEndDate: { required: '' },
    },
    education: {
      degree: {
        required: 'instant_apply_submit_error_degree_empty_field',
        validate: 'instant_apply_submit_error_degree_validation_field',
      },
      institution: {
        required: 'instant_apply_submit_error_school_empty_field',
        validate: 'instant_apply_submit_error_school_validation_field',
      },
      startDate: { required: 'instant_apply_submit_error_degree_from_empty_field' },
      endDate: { required: 'instant_apply_submit_error_degree_to_empty_field' },
      isPresentEndDate: { required: '' },
    },
  },
};

type SubmitFieldErrorsType = Record<keyof AvailableFormFieldValues, FieldError>;

type TrackSubmitValidationErrorsArg = {
  submitFieldErrors: SubmitFieldErrorsType;
  jobGlobalId: string;
  isMobile?: boolean;
  userId?: string;
};

const submitValidationErrors = (
  errorObject: SubmitFieldErrorsType,
  trackingTokensObject: SubmitValidationValues,
  callback: (string) => void
): void => {
  if (errorObject !== null && errorObject !== undefined) {
    Object.keys(errorObject).forEach((key) => {
      if (key in trackingTokensObject) {
        if (trackingTokensObject[key][errorObject[key].type] !== undefined) {
          callback(trackingTokensObject[key][errorObject[key].type]);
        } else {
          submitValidationErrors(errorObject[key], trackingTokensObject[key], callback);
        }
      }
    });
  }
};

export function trackSubmitValidationErrors({
  submitFieldErrors,
  jobGlobalId,
  isMobile = false,
  userId,
}: TrackSubmitValidationErrorsArg): void {
  const submitPropTrackActionErrorsEvent = (item) => {
    trackEvent('PropTrackAction', {
      PropFormList: 'jobs_apply_form_error',
      PropTrackActionList: item,
    });
  };

  const submitNwtContextErrorsEvent = (item) => {
    trackNwtEvent({
      context: item,
      event: 'error_shown',
      item_urn: jobGlobalId,
      page: 'jobs/apply',
      channel: isMobile ? 'webm' : 'web',
      user_id: userId,
    });
  };

  submitValidationErrors(submitFieldErrors, propTrackActionErrorsByFieldName, submitPropTrackActionErrorsEvent);
  submitValidationErrors(submitFieldErrors, nwtContextErrorsByFieldName, submitNwtContextErrorsEvent);
}
